import { useEffect, useRef, useState } from 'react'
import { Loader2, Send, Sparkles, X } from 'lucide-react'
import { useDesignAgent } from './hooks/useDesignAgent'
import { ChatMessage } from './ChatMessage'
import { TemplateSelector } from './TemplateSelector'
import type { UIMessage } from 'ai'

type TextPart = { type: 'text'; text: string }
type ToolInvocationPart = {
  type: 'tool-invocation'
  toolInvocation: {
    toolName: string
    toolCallId: string
    args: unknown
    state: 'partial-call' | 'call' | 'result'
    result?: unknown
  }
}
type MessagePart = TextPart | ToolInvocationPart | { type: string }

type AIChatPanelProps = {
  isOpen: boolean
  onClose: () => void
}

export const AIChatPanel = ({ isOpen, onClose }: AIChatPanelProps) => {
  const { messages, handleSubmit, isLoading, error, isReady, clearChat } = useDesignAgent()

  const [input, setInput] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    // Apply template prefix if selected
    const templatePrefix = selectedTemplate ? getTemplatePrefix(selectedTemplate) : ''
    const fullMessage = templatePrefix + input

    // Send the message and clear input
    await handleSubmit(fullMessage)
    setInput('')
    setSelectedTemplate(null)
  }

  const getTemplatePrefix = (templateId: string): string => {
    const prefixes: Record<string, string> = {
      birthday: 'Maak een feestelijke verjaardagskaart met de tekst: ',
      wedding: 'Maak een elegante huwelijksdesign met de namen: ',
      baby: 'Maak een schattige geboortekaart voor: ',
      thank_you: 'Maak een bedankkaartje met de tekst: ',
    }
    return prefixes[templateId] || ''
  }

  // Extract text content from message parts
  const getMessageContent = (msg: UIMessage): string => {
    if (!msg.parts) return ''

    const textParts = (msg.parts as MessagePart[]).filter(
      (part): part is TextPart => part.type === 'text',
    )

    return textParts.map((part) => part.text).join('')
  }

  // Extract tool invocations from message parts
  const getToolInvocations = (msg: UIMessage) => {
    if (!msg.parts) return []

    return (msg.parts as MessagePart[])
      .filter((part): part is ToolInvocationPart => part.type === 'tool-invocation')
      .map((part) => ({
        toolName: part.toolInvocation.toolName,
        args: part.toolInvocation.args as Record<string, unknown>,
        state: part.toolInvocation.state,
        result: part.toolInvocation.state === 'result' ? part.toolInvocation.result : undefined,
      }))
  }

  // Convert messages to the format expected by ChatMessage
  const formattedMessages = (messages as UIMessage[]).map((msg) => ({
    id: msg.id,
    role: msg.role as 'user' | 'assistant' | 'system',
    content: getMessageContent(msg),
    toolInvocations: getToolInvocations(msg),
  }))

  if (!isOpen) return null

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col border-l border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">AI Design Assistent</span>
        </div>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {formattedMessages.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 mx-auto text-purple-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Hallo! Ik ben je design assistent
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Vertel me wat je wilt maken en ik help je met het ontwerp.
            </p>
            <TemplateSelector
              selectedTemplate={selectedTemplate}
              onSelectTemplate={setSelectedTemplate}
            />
          </div>
        ) : (
          <>
            {formattedMessages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Bezig met ontwerpen...</span>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-100">
          <p className="text-sm text-red-600">Er ging iets mis. Probeer het opnieuw.</p>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        {/* Template prefix preview */}
        {selectedTemplate && (
          <div className="text-xs text-purple-600 mb-2 flex items-center gap-1">
            <span>{getTemplatePrefix(selectedTemplate)}</span>
            <button
              onClick={() => setSelectedTemplate(null)}
              className="text-purple-400 hover:text-purple-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <form onSubmit={handleFormSubmit} className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              selectedTemplate ? 'Vul hier je tekst in...' : 'Beschrijf wat je wilt maken...'
            }
            disabled={!isReady || isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={!input.trim() || !isReady || isLoading}
            className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>

        {/* Quick actions when no template selected */}
        {!selectedTemplate && formattedMessages.length > 0 && (
          <div className="mt-2 flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedTemplate('birthday')}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
            >
              + Verjaardag
            </button>
            <button
              onClick={() => setSelectedTemplate('thank_you')}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
            >
              + Bedankt
            </button>
            <button
              onClick={clearChat}
              className="text-xs px-2 py-1 text-gray-400 hover:text-gray-600 transition-colors ml-auto"
            >
              Wis chat
            </button>
          </div>
        )}
      </div>

      {/* Not ready warning */}
      {!isReady && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
          <div className="text-center p-4">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-2" />
            <p className="text-gray-600">Design laden...</p>
          </div>
        </div>
      )}
    </div>
  )
}
